import { MetaInfoViewStatus } from 'streams/views/mainContent/types';
import {
  CantMigrateSlidesPopover,
  ContactYourAdminLink,
  CreatorNameAndEmailContainer,
  DeprecatedSlideNotificationDetailsContainer,
  LeftBoundFlexContainerNoTextWrap,
  NotificationMessageContainer,
  SlideCreatedByEmail,
  SlideCreatedByInfoContainer,
  SlideCreatedByName,
  SlideCreatedByPersonIcon,
  ViewMigrationGuideLinkWithProps,
  ViewStatusTextBadgeContainer,
  WhyCantIMigrateSlidesLabel,
} from 'viewDeprecation/atoms';
import {
  DeprecatedSlideNotificationDetailsArgs,
  DeprecationPhasesInPresentationScope,
  OverallPresentationViewInfo,
  PresentationSlideViewInfo,
  SlidePermisionsInPresentationScope,
} from 'viewDeprecation/types';
import { ViewStatusTextBadge } from 'views/ViewStatusTextBadge';
import { WithUncontrolledPopover } from '@ardoq/popovers';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';

type SlideCreatorDetailsProps = {
  createdByName: string;
  createdByEmail: string;
  showCreatorDetails?: boolean;
  i18nLocale?: I18nLocale;
};
export const SlideCreatorDetails = ({
  createdByName,
  createdByEmail,
  showCreatorDetails,
  i18nLocale,
}: SlideCreatorDetailsProps) => (
  <SlideCreatedByInfoContainer>
    <SlideCreatedByPersonIcon />
    <CreatorNameAndEmailContainer>
      {showCreatorDetails ? (
        <>
          <SlideCreatedByName>{createdByName}</SlideCreatedByName>
          <SlideCreatedByEmail>{createdByEmail}</SlideCreatedByEmail>
        </>
      ) : i18nLocale ? (
        <SlideCreatedByName>
          <ContactYourAdminLink i18nLocale={i18nLocale} />
        </SlideCreatedByName>
      ) : null}
    </CreatorNameAndEmailContainer>
  </SlideCreatedByInfoContainer>
);

type DetailsProps = DeprecatedSlideNotificationDetailsArgs & WithI18nLocale;

export const DeprecatedSlideNotificationDetails = ({
  deprecatedSlides,
  canEditPresentation,
  overallPresentationViewInfo,
  slidesMigrationInfo,
  isCurrentUserOrgAdmin,
  i18nLocale,
}: DetailsProps) => {
  const canCreatorMigrate = (slideId: string) =>
    !!slidesMigrationInfo?.[slideId]?.creatorCanMigrate;

  const soonToBeDiscontinuedNoPermissions: PresentationSlideViewInfo[] = [];
  const discontinuedNoPermissions: PresentationSlideViewInfo[] = [];

  const isMixedPhase =
    overallPresentationViewInfo.overallDeprecationPhase ===
    DeprecationPhasesInPresentationScope.MIXED_PHASES;

  deprecatedSlides.forEach(slideInfo => {
    if (!canEditPresentation || !slideInfo.hasFullAccess) {
      if (slideInfo.viewStatus === MetaInfoViewStatus.SOON_TO_BE_DISCONTINUED) {
        soonToBeDiscontinuedNoPermissions.push(slideInfo);
      } else if (slideInfo.viewStatus === MetaInfoViewStatus.DISCONTINUED) {
        discontinuedNoPermissions.push(slideInfo);
      }
    }
  });

  if (isCurrentUserOrgAdmin) return null;
  return soonToBeDiscontinuedNoPermissions.length ||
    discontinuedNoPermissions.length ? (
    <DeprecatedSlideNotificationDetailsContainer>
      {isMixedPhase && soonToBeDiscontinuedNoPermissions.length ? (
        <NotificationMessageContainer>
          <ViewStatusTextBadgeContainer>
            <ViewStatusTextBadge
              viewStatus={MetaInfoViewStatus.SOON_TO_BE_DISCONTINUED}
            />
          </ViewStatusTextBadgeContainer>
        </NotificationMessageContainer>
      ) : null}
      {soonToBeDiscontinuedNoPermissions?.map(
        ({ slideName, createdByName, createdByEmail, slideId }) => (
          <div key={slideId}>
            <div>{slideName}:</div>
            <SlideCreatorDetails
              createdByName={createdByName}
              createdByEmail={createdByEmail}
              showCreatorDetails={canCreatorMigrate(slideId)}
              i18nLocale={i18nLocale}
            />
          </div>
        )
      )}
      {isMixedPhase && discontinuedNoPermissions.length ? (
        <NotificationMessageContainer>
          <ViewStatusTextBadgeContainer>
            <ViewStatusTextBadge viewStatus={MetaInfoViewStatus.DISCONTINUED} />
          </ViewStatusTextBadgeContainer>
        </NotificationMessageContainer>
      ) : null}
      {discontinuedNoPermissions?.map(
        ({ slideName, createdByName, createdByEmail, slideId }) => (
          <div key={slideId}>
            <div>{slideName}:</div>
            <SlideCreatorDetails
              createdByName={createdByName}
              createdByEmail={createdByEmail}
              showCreatorDetails={canCreatorMigrate(slideId)}
              i18nLocale={i18nLocale}
            />
          </div>
        )
      )}
      {overallPresentationViewInfo.overallPermissionsLevel !==
        SlidePermisionsInPresentationScope.FULL_PERMISSIONS && (
        <WithUncontrolledPopover
          content={<CantMigrateSlidesPopover i18nLocale={i18nLocale} />}
        >
          <WhyCantIMigrateSlidesLabel>
            {t(
              i18nLocale,
              'viewDeprecation:notification.whyCantIMigrate',
              "Why can't I migrate slides?"
            )}
          </WhyCantIMigrateSlidesLabel>
        </WithUncontrolledPopover>
      )}
    </DeprecatedSlideNotificationDetailsContainer>
  ) : null;
};

type CallToActionProps = {
  permissions: SlidePermisionsInPresentationScope;
  onlyAdminsCanMigrate: boolean;
  creatorCanPartiallyMigrate: boolean;
  isCurrentUserCreator: boolean;
  i18nLocale: I18nLocale;
};
const CallToActionMessage = ({
  permissions,
  onlyAdminsCanMigrate,
  creatorCanPartiallyMigrate,
  isCurrentUserCreator,
  i18nLocale,
}: CallToActionProps) => {
  if (onlyAdminsCanMigrate) {
    return (
      <LeftBoundFlexContainerNoTextWrap>
        <ContactYourAdminLink i18nLocale={i18nLocale} />
        <span>
          &nbsp;
          {t(
            i18nLocale,
            'viewDeprecation:countdown.contactAdminSuffix',
            'to migrate slides to the new view.'
          )}
        </span>
      </LeftBoundFlexContainerNoTextWrap>
    );
  }
  if (permissions === SlidePermisionsInPresentationScope.FULL_PERMISSIONS) {
    return (
      <NotificationMessageContainer>
        <ViewMigrationGuideLinkWithProps i18nLocale={i18nLocale} />
      </NotificationMessageContainer>
    );
  } else if (
    permissions === SlidePermisionsInPresentationScope.LIMITED_PERMISSIONS
  ) {
    return (
      <NotificationMessageContainer>
        {creatorCanPartiallyMigrate
          ? t(
              i18nLocale,
              'viewDeprecation:notification.contactCreatorOrAdmin',
              'Contact the slide creator or admin to migrate slides.'
            )
          : t(
              i18nLocale,
              'viewDeprecation:notification.contactCreator',
              'Contact the slide creator to migrate slides.'
            )}
      </NotificationMessageContainer>
    );
  } else if (
    permissions === SlidePermisionsInPresentationScope.MIXED_PERMISSIONS
  ) {
    const recipient =
      isCurrentUserCreator || onlyAdminsCanMigrate
        ? t(
            i18nLocale,
            'viewDeprecation:notification.mixedAccess.yourAdmin',
            'your admin'
          )
        : creatorCanPartiallyMigrate
          ? t(
              i18nLocale,
              'viewDeprecation:notification.mixedAccess.creatorOrAdmin',
              'the slide creator or your admin'
            )
          : t(
              i18nLocale,
              'viewDeprecation:notification.mixedAccess.creator',
              'the slide creator'
            );
    return (
      <>
        <NotificationMessageContainer>
          <ViewMigrationGuideLinkWithProps i18nLocale={i18nLocale} />
        </NotificationMessageContainer>
        <NotificationMessageContainer>
          {t(
            i18nLocale,
            'viewDeprecation:notification.mixedAccess.intro',
            { recipient },
            "In cases where you don't have workplace access, contact {{recipient}} to migrate."
          )}
        </NotificationMessageContainer>
      </>
    );
  }

  return null;
};

type SolutionAdviceArgs = {
  isCurrentUserCreator: boolean;
  overallPresentationViewInfo: OverallPresentationViewInfo;
  creatorCanMigrateNothing: boolean;
  creatorCanMigrateSome: boolean;
  i18nLocale: I18nLocale;
};
export const SolutionAdvice = ({
  isCurrentUserCreator,
  overallPresentationViewInfo,
  creatorCanMigrateNothing,
  creatorCanMigrateSome,
  i18nLocale,
}: SolutionAdviceArgs) => (
  <>
    {overallPresentationViewInfo.overallPermissionsLevel !== undefined && (
      <CallToActionMessage
        permissions={overallPresentationViewInfo.overallPermissionsLevel}
        onlyAdminsCanMigrate={creatorCanMigrateNothing}
        creatorCanPartiallyMigrate={creatorCanMigrateSome}
        isCurrentUserCreator={isCurrentUserCreator}
        i18nLocale={i18nLocale}
      />
    )}
    {creatorCanMigrateNothing ? (
      <WithUncontrolledPopover
        content={<CantMigrateSlidesPopover i18nLocale={i18nLocale} />}
      >
        <WhyCantIMigrateSlidesLabel>
          {t(
            i18nLocale,
            'viewDeprecation:notification.whyCantIMigrate',
            "Why can't I migrate slides?"
          )}
        </WhyCantIMigrateSlidesLabel>
      </WithUncontrolledPopover>
    ) : null}
  </>
);
